import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, NavLink } from 'react-router-dom';
import { Activity, Video, Image, BarChart3, Settings } from 'lucide-react';
import Dashboard from './pages/Dashboard';
import StreamProcessor from './pages/StreamProcessor';
import ImageDetection from './pages/ImageDetection';
import SettingsPage from './pages/Settings';
import { getApiBaseUrl, getHealth } from './utils/api';
import './App.css';

function App() {
  const [healthStatus, setHealthStatus] = useState('connecting'); // connecting | healthy | degraded | warming | offline

  useEffect(() => {
    const checkHealth = async () => {
      try {
        const data = await getHealth();
        setHealthStatus(
          data?.status === 'healthy'
            ? 'healthy'
            : data?.status === 'degraded'
              ? 'degraded'
              : data?.status === 'warming'
                ? 'warming'
                : 'healthy'
        );
      } catch (error) {
        const httpStatus = error.response?.status;
        if (httpStatus === 503) {
          setHealthStatus('warming');
        } else if (error.request) {
          setHealthStatus('offline');
        } else {
          setHealthStatus('offline');
        }
        console.error('Health check error:', {
          apiBaseUrl: getApiBaseUrl(),
          message: error.message,
          status: httpStatus,
        });
      }
    };

    checkHealth();
    const interval = setInterval(checkHealth, 30000);
    return () => clearInterval(interval);
  }, []);

  const statusColor = {
    connecting: 'text-gray-300',
    healthy: 'text-green-400',
    degraded: 'text-amber-400',
    warming: 'text-yellow-300',
    offline: 'text-red-400',
  }[healthStatus];

  const statusLabel = {
    connecting: 'connecting…',
    healthy: 'online',
    degraded: 'degraded',
    warming: 'warming up…',
    offline: 'offline',
  }[healthStatus];

  const navItems = [
    { to: '/', label: 'Dashboard', icon: BarChart3, end: true },
    { to: '/stream', label: 'Stream', icon: Video },
    { to: '/image', label: 'Image', icon: Image },
    { to: '/settings', label: 'Settings', icon: Settings },
  ];

  const renderNavLink = ({ to, label, icon: Icon, end = false, mobile = false }) => (
    <NavLink
      key={to}
      to={to}
      end={end}
      className={({ isActive }) =>
        mobile
          ? `flex min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded-lg px-2 py-2 text-[11px] font-medium transition-colors ${
              isActive ? 'bg-cyan-600 text-white' : 'text-gray-300 hover:bg-slate-800'
            }`
          : `flex items-center space-x-3 rounded-lg px-4 py-3 transition-colors ${
              isActive ? 'bg-cyan-600 text-white' : 'text-gray-300 hover:bg-slate-800'
            }`
      }
    >
      <Icon className={mobile ? 'h-5 w-5 shrink-0' : 'h-5 w-5 shrink-0'} />
      <span className={mobile ? 'truncate' : ''}>{label}</span>
    </NavLink>
  );

  return (
    <Router>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        {/* Navigation */}
        <nav className="sticky top-0 z-30 border-b border-slate-700 bg-slate-900 shadow-lg">
          <div className="mx-auto max-w-7xl px-4 py-3 md:py-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div className="flex min-w-0 items-center space-x-2">
                <Activity className="h-7 w-7 shrink-0 text-cyan-400 md:h-8 md:w-8" />
                <h1 className="truncate text-lg font-bold text-white sm:text-xl md:text-2xl">Retail Analytics Dashboard</h1>
              </div>
              <div className="flex items-center">
                <div className="min-w-0 truncate text-xs text-gray-300 sm:text-sm" title={`API: ${getApiBaseUrl()}`}>
                  Backend:{' '}
                  <span className={statusColor}>{statusLabel}</span>
                  {healthStatus === 'warming' && (
                    <span className="ml-2 text-xs text-yellow-300/70">(loading model)</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </nav>

        {/* Main Content */}
        <div className="flex min-h-[calc(100vh-65px)]">
          {/* Sidebar */}
          <div className="hidden w-64 shrink-0 border-r border-slate-700 bg-slate-900 p-4 md:block">
            <div className="space-y-2">
              {navItems.map(item => renderNavLink(item))}
            </div>
          </div>

          {/* Content Area */}
          <div className="min-w-0 flex-1 p-4 pb-24 md:p-8">
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/stream" element={<StreamProcessor />} />
              <Route path="/image" element={<ImageDetection />} />
              <Route path="/settings" element={<SettingsPage />} />
            </Routes>
          </div>
        </div>
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-700 bg-slate-950/95 px-2 py-2 backdrop-blur md:hidden">
          <div className="mx-auto flex max-w-md gap-1">
            {navItems.map(item => renderNavLink({ ...item, mobile: true }))}
          </div>
        </div>
      </div>
    </Router>
  );
}

export default App;
